import { useState, useCallback } from 'react';
import { logOutAPI } from '@common/api/api';
import { analyticsService } from '@common/analytics';
import { hotJarReset } from '../../../actions/hotjar';
import { removeCookie } from '../../../config/cookies_action';
import { log, LOG_LEVELS } from '../../../../common/config/app_logger';

const logoutZendesk = () => {
  try {
    const { zE } = window;
    if (typeof zE === 'function') zE('messenger', 'logoutUser');
  } catch (err) {
    log(LOG_LEVELS.ERROR, 'Auth', 'Zendesk logoutUser failed during sign out', err);
  }
};

const authCookieName = 'is_logged_in';

const getCookieRootDomain = () => {
  const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
  return `.${ rootDomain }`;
};

export const useLogout = (callbacks = {}) => {
  const {
    onHomePageLoad = () => {},
    onAnonymousUserSet = () => {},
    onLogoutClear = () => {},
    onDynamicConfigSet = () => {}
  } = callbacks;
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const performCleanup = useCallback((clientSettings) => {
    analyticsService.trackEvent('sign out');
    analyticsService.reset();
    onHomePageLoad();
    hotJarReset();
    onAnonymousUserSet();
    logoutZendesk();
    removeCookie({
      cookieName: authCookieName,
      cookieOptions: { path: '/', domain: getCookieRootDomain() }
    });
    onLogoutClear();
    if (clientSettings) {
      onDynamicConfigSet(clientSettings);
    }
  }, []);

  const clientSideLogout = useCallback(() => {
    try {
      performCleanup();
      setSuccess(true);
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'Auth', 'Error during client-side logout', error);
      window.location.reload();
    }
  }, [performCleanup]);

  const logout = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    setSuccess(false);

    try {
      const response = await logOutAPI();
      performCleanup(response?.clientSettings);
      setSuccess(true);
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'Auth', 'Error logging out', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loading, performCleanup]);

  const reset = useCallback(() => {
    setLoading(false);
    setSuccess(false);
  }, []);

  return {
    loading,
    success,
    logout,
    clientSideLogout,
    reset
  };
};
